import React, { useRef, useEffect, useCallback } from "react";
import * as d3 from "d3";
import ResizeObserver from "resize-observer-polyfill";
import { COLOR_MAP } from "../colorMap";

const RaagSunburst = ({ raags, highlightedRaag, onFilterSelect, onRaagSelect, muted }) => {
  const ref = useRef();
  const containerRef = useRef();
  const hasAnimatedRef = useRef(false);

  const drawChart = useCallback(() => {
    const container = containerRef.current;
    if (!container || !raags.length) return;

    const containerRect = container.getBoundingClientRect();
    const width = Math.min(containerRect.width, 800);
    const radius = Math.max(width / 2, 200);

    const playClick = () => {
      if (!muted) {
        const audio = new Audio("/sounds/dial-click.mp3");
        audio.volume = 0.3;
        audio.play().catch(() => {});
      }
    };

    const svg = d3.select(ref.current);
    svg.selectAll("*").remove();

    svg
      .attr("viewBox", [0, 0, width, width])
      .attr("preserveAspectRatio", "xMidYMid meet")
      .style("overflow", "visible");

    const g = svg.append("g").attr("transform", `translate(${width / 2}, ${width / 2})`);

    const centerLabel = g
      .append("text")
      .attr("text-anchor", "middle")
      .attr("alignment-baseline", "middle")
      .attr("class", "center-label")
      .style("font-size", "20px")
      .style("font-weight", "bold")
      .style("fill", "#333");

      const buildHierarchy = (raags) => {
        const root = { Name: "Raag", children: [] };
      
        const validRaags = raags.filter(
          (raag) =>
            raag.Name &&
            raag.Season &&
            raag.Time &&
            raag.Mood &&
            (Array.isArray(raag.Mood) ? raag.Mood.length > 0 : true)
        );
      
        validRaags.forEach(({ Season, Time, Mood, Name }) => {
          let s = root.children.find((x) => x.Name === Season);
          if (!s) {
            s = { Name: Season, children: [] };
            root.children.push(s);
          }
      
          let t = s.children.find((x) => x.Name === Time);
          if (!t) {
            t = { Name: Time, children: [] };
            s.children.push(t);
          }
      
          const Moods = Array.isArray(Mood) ? Mood : [Mood];
          Moods.forEach((m) => {
            let mo = t.children.find((x) => x.Name === m);
            if (!mo) {
              mo = { Name: m, children: [] };
              t.children.push(mo);
            }
            mo.children.push({ Name });
          });
        });
      
        return root;
      };
      

    const root = d3
      .hierarchy(buildHierarchy(raags))
      .sum((d) => (d.children ? 0 : 1))
      .sort((a, b) => b.value - a.value);

    const partition = d3.partition().size([2 * Math.PI, radius]);
    partition(root);

    const arc = d3
      .arc()
      .startAngle((d) => d.x0)
      .endAngle((d) => d.x1)
      .innerRadius((d) => d.y0)
      .outerRadius((d) => d.y1);

    const getColor = (d) => COLOR_MAP[d.data.Name] || "#ccc";
    const isFittable = (d) => d.x1 - d.x0 > 0.1;

    const allNodes = root.descendants().filter((d) => d.depth);
    const maxDepth = d3.max(allNodes, (d) => d.depth);

    const depthGroups = g
      .selectAll("g.depth")
      .data(d3.range(1, maxDepth + 1))
      .join("g")
      .attr("class", (d) => `depth depth-${d}`);

    for (let depth = 1; depth <= maxDepth; depth++) {
      const ring = depthGroups.filter(`.depth-${depth}`);
      const nodes = allNodes.filter((d) => d.depth === depth);

      ring
        .selectAll("path")
        .data(nodes)
        .join("path")
        .attr("stroke", "#fff")
        .attr("fill", (d) =>
          d.children ? getColor(d) : d.data.Name === highlightedRaag ? "#ff0000" : getColor(d)
        )
        .attr("d", arc)
        .attr("transform", (d) => {
          const [x, y] = arc.centroid(d);
          return d.depth > 1 ? `translate(${x * 0.05}, ${y * 0.05})` : "";
        })
        .style("cursor", "pointer")
        .on("mouseover", function (e, d) {
          playClick();

          centerLabel
            .text(d.data.Name)
            .style("font-size", d.depth === 1 ? "24px" : d.depth === 2 ? "18px" : "14px")
            .style("font-weight", d.depth === 1 ? "700" : d.depth === 2 ? "600" : "400");

          d3.select(this)
            .transition()
            .duration(300)
            .ease(d3.easeCubicOut)
            .attr("transform", () => {
              const [x, y] = arc.centroid(d);
              return `translate(${x * 0.08}, ${y * 0.08}) scale(1.05)`;
            });
        })
        .on("mouseout", function (e, d) {
          centerLabel.text("");

          d3.select(this)
            .transition()
            .duration(300)
            .ease(d3.easeCubicIn)
            .attr("transform", () => {
              const [x, y] = arc.centroid(d);
              return d.depth > 1 ? `translate(${x * 0.05}, ${y * 0.05}) scale(1)` : "scale(1)";
            });
        })
        .on("click", (e, d) => {
          const path = d.ancestors().map((n) => n.data.Name).reverse().slice(1);
          if (!d.children && onRaagSelect) {
            onRaagSelect(d.data.Name);
          } else if (onFilterSelect) {
            onFilterSelect(path);
          }
        });

      if (!hasAnimatedRef.current) {
        ring
          .attr("opacity", 0)
          .attr("transform", `rotate(${depth * 45})`)
          .transition()
          .delay(depth * 100)
          .duration(1000)
          .ease(d3.easeCubicOut)
          .attr("opacity", 1)
          .attr("transform", "rotate(0)")
          .on("start", () => playClick());
      }
    }

    hasAnimatedRef.current = true;

    g.selectAll("text.internal")
      .data(allNodes.filter((d) => d.depth && isFittable(d)))
      .join("text")
      .attr("class", "internal")
      .attr("transform", (d) => {
        const x = ((d.x0 + d.x1) / 2) * (180 / Math.PI);
        const y = (d.y0 + d.y1) / 2;
        return `rotate(${x - 90}) translate(${y},0) rotate(${x < 180 ? 0 : 180})`;
      })
      .attr("dy", "0.35em")
      .attr("x", (d) => ((d.x0 + d.x1) / 2 < Math.PI ? 6 : -6))
      .attr("text-anchor", (d) => ((d.x0 + d.x1) / 2 < Math.PI ? "start" : "end"))
      .style("font-size", "11px")
      .style("fill", "#333")
      .text((d) => d.data.Name);
  }, [raags, highlightedRaag, muted, onFilterSelect, onRaagSelect]);

  useEffect(() => {
    if (!raags.length) return;
    const container = containerRef.current;
    if (!container) return;

    hasAnimatedRef.current = false;

    const resizeObserver = new ResizeObserver(() => {
      drawChart();
    });

    resizeObserver.observe(container);
    drawChart();

    return () => resizeObserver.disconnect();
  }, [raags, drawChart]);

  return (
    <div
      ref={containerRef}
      style={{
        width: "100%",
        overflow: "visible",
        display: "flex",
        justifyContent: "center"
      }}
    >
      <svg
        ref={ref}
        style={{ width: "100%", height: "auto", maxWidth: "800px" }}
      />
    </div>
  );
};

export default RaagSunburst;
